<template>
  <PartnerRestaurantSelector v-if="showRestaurantSelector" :restaurants="partnerRestaurants" :busy="restaurantSelectionBusy" :error="restaurantSelectionError" @select="choosePartnerRestaurant" @cancel="cancelRestaurantSelection" />
  <div class="row m-0 p-0">
    <div class="col-12 col-lg-6 px-0 login-image-column">
      <img class="login-image" :src="loginImage" style="height: 100%;" />
      <div class="login-image-overlay">
        <img class="login-image-overlay__logo" :src="loginLogoWhite" alt="Eat with Us" />
      </div>
    </div>
    <div class="d-flex align-items-center col-12 col-lg-6 login-div pb-5" style="background-color: #F9F9F9 !important;">
      <div class="login-mobile-hero">
        <div class="login-mobile-hero__media" :style="{ backgroundImage: `url(${loginImage})` }">
          <div class="login-mobile-hero__overlay"></div>
          <div class="login-mobile-hero__content">
            <img class="login-mobile-hero__logo" :src="loginLogoWhite" alt="Eat with Us" />
            <div class="login-mobile-hero__copy">
              <p class="login-mobile-hero__eyebrow">Acceso al TPV</p>
              <h1 class="login-mobile-hero__title">Entra de forma rápida y segura</h1>
              <p class="login-mobile-hero__subtitle">
                Accede con tu cuenta del restaurante.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div v-if="showQuickAccessPanel" class="login-mode-card">
        <div class="login-mode-card__copy">
          <p class="login-mode-card__eyebrow">Acceso al TPV</p>
          <h4 class="login-mode-card__title">Elige cómo quieres entrar</h4>
          <p class="login-mode-card__subtitle">
            El acceso rápido usa usuarios ya guardados en este dispositivo.
          </p>
        </div>
        <div class="login-mode-switch" role="tablist" aria-label="Modo de acceso">
          <button
            type="button"
            class="btn rounded-pill py-2"
            :class="{ 'login-mode-switch__button--active': loginMode === 'quick' }"
            @click="setLoginMode('quick')"
            role="tab"
            :aria-selected="loginMode === 'quick'"
          >
            <i class="fa-solid fa-bolt me-2"></i>
            Acceso rápido
          </button>
          <button
            type="button"
            class="btn rounded-pill py-2"
            :class="{ 'login-mode-switch__button--active': loginMode === 'normal' }"
            @click="setLoginMode('normal')"
            role="tab"
            :aria-selected="loginMode === 'normal'"
          >
            <i class="fa-solid fa-user me-2"></i>
            Acceso normal
          </button>
        </div>
      </div>

      <!-- Si un chequeo de conectividad transitorio deja el modo en quick
           pero ya no hay panel rápido visible, conserva siempre una vía de
           entrada normal en lugar de dejar la columna vacía. -->
      <!-- Mantener el formulario montado evita perder lo escrito si cambia el
           estado de conectividad mientras se está haciendo login. -->
      <form v-show="loginMode === 'normal' || !showQuickAccessPanel" autocomplete="on" @submit.prevent="IniciarSesion()">
        <div class="mt-4">
          <div class="offline-login__divider">
            <span>Acceso normal</span>
          </div>
          <p class="text-center text-muted mb-4">
            Introduzca su email y contraseña para acceder.
          </p>
          <div class="login-input mx-3 mt-3">
            <i class="fa-solid fa-user "></i>
            <input
              id="login-email"
              type="text"
              placeholder="Introduzca su email"
              v-model="userLogin.email"
              autocomplete="username"
              name="username"
              inputmode="email"
            >
          </div>
          <div class="login-input mx-3 mt-3">
            <i class="fa-solid fa-lock "></i>
            <input
              id="login-password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Introduzca su contraseña"
              v-model="userLogin.password"
              autocomplete="current-password"
              name="password"
              maxlength="30"
            />
            <button
              type="button"
              class="login-password-toggle"
              :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              :title="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              @click="showPassword = !showPassword"
            >
              <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
            </button>
          </div>
          <p class="mt-4 text-center alert alert-danger py-1 w-75 mx-auto fw-semibold" v-if="loginError" style="font-size: 1rem;">
            {{ loginError }}
          </p>

          <div class="d-flex justify-content-center mt-4 w-100">
            <button class="btn text-white px-5 fw-bold rounded-pill" style="background-color: var(--primary-color); font-size: 1.3rem;" type="submit">
              Iniciar sesión
            </button>
          </div>

          <div class="decorative-text mt-4 pt-2">
            <a @click="changing_pass=true" style="cursor: pointer;">¿Olvidaste la contraseña?</a>
          </div>
        </div>
      </form>

      <div v-show="loginMode === 'quick' && showQuickAccessPanel" class="offline-login">
        <div class="offline-login__divider">
          <span>Acceso rápido</span>
        </div>
        <p class="text-center text-muted mb-4">
          Selecciona un usuario usado anteriormente en este dispositivo. Al tocarlo, aparece el teclado PIN.
        </p>

        <div v-if="offlineRestaurants.length > 1" class="mb-3">
          <label class="form-label fw-semibold">Restaurante</label>
          <select v-model="selectedRestaurantId" class="form-select">
            <option v-for="restaurant in offlineRestaurants" :key="restaurant.restaurant_id" :value="restaurant.restaurant_id">
              {{ restaurant.restaurant_name || restaurant.restaurant_id }}
            </option>
          </select>
        </div>

        <div v-if="offlineUsers.length" class="mb-3">
          <label class="form-label fw-semibold">Usuario</label>
          <div class="offline-users">
            <div
              v-for="user in offlineUsers"
              :key="user.id"
              :class="['offline-user', { 'offline-user--active': selectedOfflineUserId === user.user_id }]"
            >
              <button
                type="button"
                class="offline-user__main"
                @click="selectOfflineUser(user)"
              >
                <strong>{{ user.display_name }}</strong>
                <span>{{ user.username_or_email_masked }}</span>
              </button>
              <button
                type="button"
                class="offline-user__delete"
                :title="`Eliminar ${user.display_name}`"
                :aria-label="`Eliminar ${user.display_name}`"
                @click="removeOfflineUser(user)"
              >
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <p v-if="offlineError" class="mt-3 text-center alert alert-danger py-2 fw-semibold">
          {{ offlineError }}
        </p>
        <p v-else-if="!offlineUsers.length" class="mt-3 text-center alert alert-warning py-2 fw-semibold">
          {{ offlineUsersEmptyMessage }}
        </p>
      </div>

      <spinner-component id="spinner" class="d-none"></spinner-component>
    </div>

    <div v-if="changing_pass" class="modal fade show d-block z-3" tabindex="-1">
      <div @click="changing_pass = false" class="h-100 w-100 position-fixed z-1"></div>
      <div class="modal-dialog d-flex align-items-center justify-content-center z-2">
        <div class="modal-content p-5">
          <div v-if="!sended">
            <h6 class="mb-3">Introduce tu email:</h6>
            <div class="form-floating mb-3">
              <input type="email" class="form-control" id="floatingInput" v-model="email_change" autocomplete="email" name="recovery-email">
              <label for="floatingInput">Email</label>
            </div>
            <p class="text-danger">{{ response }}</p>
            <div class="row">
              <button class="col-6 mx-auto btn btn-dark" @click="ChangePass">Enviar</button>
            </div>
          </div>
          <div v-else>
            <img src="@/assets/tick.gif" alt="Email enviada" class="img-fluid w-25" />
            <h3 class="text-success mb-3">¡Petición procesada!</h3>
            <p>Te hemos enviado un correo electrónico para poder cambiar tu contraseña</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showOfflinePinSetup" class="modal fade show d-block z-3" tabindex="-1">
      <div class="h-100 w-100 position-fixed z-1" @click="skipOfflinePinSetup"></div>
      <div class="modal-dialog d-flex align-items-center justify-content-center z-2">
        <div class="modal-content p-4">
          <h5 class="fw-bold mb-2">Configurar acceso rápido</h5>
          <p class="text-muted mb-3">
            Configura un PIN local para poder entrar con este usuario en este dispositivo.
          </p>
          <div class="form-floating mb-3">
            <input
              v-model="offlineSetupPin"
              type="password"
              class="form-control"
              placeholder="PIN"
              inputmode="numeric"
              @focus="openPinKeypad('offlineSetupPin')"
              @click="openPinKeypad('offlineSetupPin')"
            >
            <label>PIN</label>
          </div>
          <div class="form-floating mb-3">
            <input
              v-model="offlineSetupPinConfirm"
              type="password"
              class="form-control"
              placeholder="Confirmar PIN"
              inputmode="numeric"
              @focus="openPinKeypad('offlineSetupPinConfirm')"
              @click="openPinKeypad('offlineSetupPinConfirm')"
            >
            <label>Confirmar PIN</label>
          </div>
          <p v-if="offlineSetupError" class="text-danger fw-semibold">{{ offlineSetupError }}</p>
          <div class="d-flex justify-content-end gap-2">
            <button type="button" class="btn btn-outline-secondary" @click="skipOfflinePinSetup">
              Ahora no
            </button>
            <button type="button" class="btn text-white" style="background-color: var(--primary-color);" @click="confirmOfflinePinSetup">
              Activar acceso rápido
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <transition name="pin-keypad-fade">
    <div v-if="showPinKeypad" class="pin-keypad-backdrop" @click="closePinKeypad">
      <div class="pin-keypad" @click.stop>
        <div class="pin-keypad__header">
          <div>
            <p class="pin-keypad__eyebrow">PIN</p>
            <h6 class="pin-keypad__title">Teclado numérico</h6>
          </div>
          <button type="button" class="pin-keypad__close" @click="closePinKeypad">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="pin-keypad__display">
          <span>{{ pinKeypadDisplay }}</span>
        </div>
        <div class="pin-keypad__grid">
          <button v-for="key in pinKeys" :key="key" type="button" class="pin-keypad__key" @click="handlePinKey(key)">
            <span v-if="key !== 'backspace'">{{ key }}</span>
            <i v-else class="fa-solid fa-delete-left"></i>
          </button>
        </div>
        <div class="pin-keypad__footer">
          <button type="button" class="pin-keypad__action pin-keypad__action--ghost" @click="clearActivePin">
            Borrar
          </button>
          <button type="button" class="pin-keypad__action pin-keypad__action--primary" @click="submitPinKeypad">
            Aceptar
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios';
import SpinnerComponent from '../components/SpinnerComponent.vue';
import { mapState } from 'vuex';
import router from '@/router';
import PartnerRestaurantSelector from '@/components/PartnerRestaurantSelector.vue';
import { getPartnerToken, getPartnerRefresh, setPartnerSession, clearPartnerSession } from '@/services/auth/partnerSessionStorage';
import { listPartnerRestaurants, selectPartnerRestaurant, restaurantAccessError } from '@/services/auth/partnerRestaurantService';
import loginImage from '@/assets/login-image.jpg'
import loginLogoWhite from '@/assets/EWU_Blanco.png'
import {
  CONNECTIVITY_MODES,
  connectivityStatus,
  shouldUseOfflineMode,
  startConnectivityMonitoring,
  isBackendUnavailableError,
  subscribeToConnectivityChanges,
} from '@/services/connectivity/connectivityService';
import { savePartnerSessionCache, getPartnerSessionCache } from '@/services/offline/partnerSessionCache';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import {
  clearOfflineSession,
  createOfflineSessionFromUser,
  deleteOfflineUserProfile,
  enableOfflineLoginForUser,
  getLastActiveRestaurantContext,
  listOfflineRestaurantsForDevice,
  listOfflineUsersByRestaurant,
  saveOfflineUserProfile,
  supportsOfflinePinSetup,
  verifyOfflineUserPin,
  isOfflineSessionActive,
} from '@/services/offline/offlineUserAccess';
import { getOrCreateDeviceId } from '@/services/localDb/device';
import { runRestaurantFullBackup } from '@/services/offline/restaurantBackupService';

export default {
  name: 'LoginPage',
  components: {
    SpinnerComponent,
    PartnerRestaurantSelector
  },
  data() {
    return {
      loginImage,
      loginLogoWhite,
      userLogin: {
        email: '',
        password: ''
      },
      saludo: '',
      changing_pass: false,
      email_change: '',
      response: '',
      sended: false,
      showPassword: false,
      loginError: '',
      showRestaurantSelector: false,
      partnerRestaurants: [],
      restaurantSelectionToken: null,
      restaurantSelectionBusy: false,
      restaurantSelectionError: '',
      offlineRestaurants: [],
      selectedRestaurantId: '',
      offlineUsers: [],
      selectedOfflineUserId: '',
      offlinePin: '',
      showOfflinePin: false,
      offlineError: '',
      showOfflinePinSetup: false,
      offlineSetupPin: '',
      offlineSetupPinConfirm: '',
      offlineSetupError: '',
      pendingOfflineUser: null,
      initialOfflinePreparationStarted: false,
      pendingLoginRoute: { name: 'TPVHub' },
      activePinField: null,
      pinKeys: ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'backspace', '0'],
      loginMode: 'normal',
      loginModeManuallySelected: false,
      connectivityUnsubscribe: null,
      lastConnectivityMode: null,
    };
  },
  computed: {
    ...mapState(['isLoggedIn']),
    ...mapState(['token']),
    ...mapState(['refresh']),
    isOfflineContext() {
      return connectivityStatus.manualOffline
        || connectivityStatus.mode === CONNECTIVITY_MODES.BROWSER_OFFLINE
        || connectivityStatus.mode === CONNECTIVITY_MODES.BACKEND_UNAVAILABLE;
    },
    showQuickAccessPanel() {
      // Un fallo puntual del health-check no debe sustituir el formulario de
      // acceso normal por el panel offline. El panel rápido solo tiene sentido
      // cuando existe al menos un usuario guardado que se pueda seleccionar.
      return OFFLINE_MODE_ENABLED && this.offlineRestaurants.length > 0;
    },
    showPinKeypad() {
      return Boolean(this.activePinField);
    },
    pinKeypadDisplay() {
      const value = this.getActivePinValue();
      return value ? '•'.repeat(value.length) : 'Introduce el PIN';
    },
    offlineUsersEmptyMessage() {
      if (this.isOfflineContext) {
        return 'No puedes iniciar sesión porque el TPV está offline y todavía no hay ningún usuario con PIN guardado en este dispositivo. Conéctate a internet e inicia sesión una vez para configurarlo.';
      }

      return 'Este dispositivo no tiene usuarios preparados para acceso rápido. Inicia sesión al menos una vez.';
    },
  },
  watch: {
    offlineUsers(newUsers) {
      if (!this.selectedOfflineUserId && newUsers.length) {
        this.selectedOfflineUserId = newUsers[0].user_id;
      }
    },
    selectedRestaurantId() {
      this.loadOfflineUsers();
    }
  },
  async mounted() {
    if (OFFLINE_MODE_ENABLED) {
      startConnectivityMonitoring();
      this.connectivityUnsubscribe = subscribeToConnectivityChanges((state) => {
        // No reconstruir las opciones de acceso cada 15 segundos.
        if (this.lastConnectivityMode === state.mode) return;
        this.lastConnectivityMode = state.mode;
        void this.loadOfflineAccessOptions();
      });
      if (!isOfflineSessionActive() && !shouldUseOfflineMode()) {
        await clearOfflineSession();
      }
    }
    this.setSaludo();
    if (this.$route.query.selectRestaurant === '1') await this.resumeRestaurantSelection();
  },
  beforeUnmount() {
    if (typeof this.connectivityUnsubscribe === 'function') {
      this.connectivityUnsubscribe();
      this.connectivityUnsubscribe = null;
    }
  },
  methods: {
    hasNormalLoginInput() {
      return Boolean(
        String(this.userLogin.email || '').trim()
        || String(this.userLogin.password || '').trim(),
      );
    },
    setLoginMode(mode) {
      this.loginMode = mode;
      this.loginModeManuallySelected = true;
    },
    showSpinner() {
      document.getElementById('spinner')?.classList.remove('d-none');
    },
    hideSpinner() {
      document.getElementById('spinner')?.classList.add('d-none');
    },
    getActivePinValue() {
      if (this.activePinField === 'offlinePin') return this.offlinePin;
      if (this.activePinField === 'offlineSetupPin') return this.offlineSetupPin;
      if (this.activePinField === 'offlineSetupPinConfirm') return this.offlineSetupPinConfirm;
      return '';
    },
    setActivePinValue(value) {
      if (this.activePinField === 'offlinePin') {
        this.offlinePin = value;
        return;
      }
      if (this.activePinField === 'offlineSetupPin') {
        this.offlineSetupPin = value;
        return;
      }
      if (this.activePinField === 'offlineSetupPinConfirm') {
        this.offlineSetupPinConfirm = value;
      }
    },
    openPinKeypad(field) {
      this.activePinField = field;
    },
    async selectOfflineUser(user) {
      this.offlineError = '';
      this.selectedOfflineUserId = user?.user_id || '';
      this.offlinePin = '';
      this.openPinKeypad('offlinePin');
    },
    closePinKeypad() {
      this.activePinField = null;
    },
    handlePinKey(key) {
      const currentValue = this.getActivePinValue();
      if (!this.activePinField) return;
      if (key === 'backspace') {
        this.setActivePinValue(currentValue.slice(0, -1));
        return;
      }
      if (/^\d$/.test(String(key))) {
        this.setActivePinValue(`${currentValue}${key}`.slice(0, 8));
      }
    },
    clearActivePin() {
      this.setActivePinValue('');
    },
    async submitPinKeypad() {
      if (this.activePinField === 'offlinePin') {
        await this.unlockQuickAccessSession();
        return;
      }
      this.closePinKeypad();
    },
    async loadOfflineAccessOptions() {
      this.offlineError = '';
      const restaurants = await listOfflineRestaurantsForDevice(getOrCreateDeviceId());
      this.offlineRestaurants = restaurants;

      const lastRestaurant = getLastActiveRestaurantContext();
      if (restaurants.length === 1) {
        this.selectedRestaurantId = restaurants[0].restaurant_id;
      } else if (lastRestaurant?.restaurant_id && restaurants.some((restaurant) => restaurant.restaurant_id === lastRestaurant.restaurant_id)) {
        this.selectedRestaurantId = lastRestaurant.restaurant_id;
      } else if (restaurants[0]?.restaurant_id) {
        this.selectedRestaurantId = restaurants[0].restaurant_id;
      } else {
        this.selectedRestaurantId = '';
      }

      await this.loadOfflineUsers();
      // No cambies de pestaña mientras el usuario está escribiendo. Cambiar
      // el v-if del formulario en ese momento desmonta los inputs y borra su
      // contenido percibido, aunque la vista no haya navegado realmente.
      const canAutoSelectMode = !this.loginModeManuallySelected && !this.hasNormalLoginInput();
      if (this.isOfflineContext && restaurants.length > 0 && canAutoSelectMode) {
        this.loginMode = 'quick';
      } else if (!this.isOfflineContext && canAutoSelectMode) {
        // Un fallo temporal del health-check no puede dejar oculto el login
        // normal cuando el backend vuelve a estar disponible.
        this.loginMode = 'normal';
      }
    },
    async loadOfflineUsers() {
      if (!this.selectedRestaurantId) {
        this.offlineUsers = [];
        this.selectedOfflineUserId = '';
        return;
      }

      this.offlineUsers = await listOfflineUsersByRestaurant({
        restaurantId: this.selectedRestaurantId,
        deviceId: getOrCreateDeviceId(),
        onlyEnabled: true,
      });
      this.selectedOfflineUserId = this.offlineUsers[0]?.user_id || '';
    },
    startInitialOfflinePreparation() {
      if (!OFFLINE_MODE_ENABLED) return;
      if (this.initialOfflinePreparationStarted) return;
      this.initialOfflinePreparationStarted = true;
      void runRestaurantFullBackup().catch((error) => {
        this.initialOfflinePreparationStarted = false;
        console.warn('No se pudo completar la preparación inicial offline tras el login.', error);
      });
    },
    async IniciarSesion() {
      this.loginError = '';
      this.showSpinner();

      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/v1/api/partner/pair`;
        const response = await axios.post(apiUrl, this.userLogin);

        if (response.data.user_admin) {
          this.$store.dispatch('setTokenAdmin', response.data);
          this.hideSpinner();
          router.push({ name: 'LoginAdmin' });
          return;
        }

        if (response.data.selection_required) {
          setPartnerSession(response.data);
          this.restaurantSelectionToken = response.data.access;
          this.partnerRestaurants = response.data.restaurants || [];
          this.restaurantSelectionError = '';
          this.showRestaurantSelector = true;
          this.hideSpinner();
          return;
        }
        await this.completePartnerLogin(response.data);
      } catch (error) {
        this.hideSpinner();
        if (shouldUseOfflineMode()) {
          await this.loadOfflineAccessOptions();
          this.loginError = '';
          return;
        }
        this.loginError = 'Credenciales incorrectas';
      }
    },
    async completePartnerLogin(session) {
        this.$store.dispatch('setToken', session);
        if (OFFLINE_MODE_ENABLED) await clearOfflineSession();

        let partnerUser = null;
        try {
          const meResponse = await axios.get(`${process.env.VUE_APP_API_URL}/v1/api/partner/me`, {
            headers: {
              Authorization: `Bearer ${session.access}`
            }
          });
          partnerUser = savePartnerSessionCache(meResponse.data) || meResponse.data;
          this.$store.commit('device/setPartnerContext', meResponse.data);
          if (OFFLINE_MODE_ENABLED) {
            const offlineUser = await saveOfflineUserProfile(partnerUser, {
              deviceId: getOrCreateDeviceId(),
              loginIdentifier: this.userLogin.email,
            });
            this.startInitialOfflinePreparation();
            if (offlineUser && supportsOfflinePinSetup() && !offlineUser.pin_hash) {
              this.pendingOfflineUser = partnerUser;
              this.showOfflinePinSetup = true;
              this.hideSpinner();
              return;
            }
          }

        } catch (meError) {
          console.warn('No se pudo preparar el usuario offline tras el login online.', meError);
        }

        this.hideSpinner();
        router.push(this.pendingLoginRoute);
    },
    async resumeRestaurantSelection() {
      this.restaurantSelectionBusy = true;
      this.showRestaurantSelector = true;
      try {
        let token = getPartnerToken();
        try {
          const list = await listPartnerRestaurants(token);
          this.partnerRestaurants = list.restaurants;
        } catch (error) {
          if (Number(error?.response?.status) !== 401 || !getPartnerRefresh()) throw error;
          const response = await axios.post(`${process.env.VUE_APP_API_URL}/v1/api/partner/refresh`, { refresh: getPartnerRefresh() });
          setPartnerSession(response.data);
          token = response.data.access;
          const list = await listPartnerRestaurants(token);
          this.partnerRestaurants = list.restaurants;
        }
        this.restaurantSelectionToken = token;
      } catch (error) {
        this.restaurantSelectionError = restaurantAccessError(error);
      } finally { this.restaurantSelectionBusy = false; }
    },
    async choosePartnerRestaurant(restaurantId) {
      this.restaurantSelectionBusy = true;
      this.restaurantSelectionError = '';
      try {
        const session = await selectPartnerRestaurant(restaurantId, this.restaurantSelectionToken);
        this.showRestaurantSelector = false;
        await this.completePartnerLogin(session);
      } catch (error) { this.restaurantSelectionError = restaurantAccessError(error); }
      finally { this.restaurantSelectionBusy = false; }
    },
    cancelRestaurantSelection() {
      this.showRestaurantSelector = false;
      this.restaurantSelectionToken = null;
      clearPartnerSession();
      this.$router.replace({ name: 'Login' });
    },
    async unlockQuickAccessSession() {
      this.offlineError = '';
      this.showSpinner();
      let verifiedUserRecord = null;

      try {
        verifiedUserRecord = await verifyOfflineUserPin({
          restaurantId: this.selectedRestaurantId,
          userId: this.selectedOfflineUserId,
          pin: this.offlinePin,
          deviceId: getOrCreateDeviceId(),
        });

        if (!verifiedUserRecord) {
          this.offlineError = 'PIN incorrecto';
          return;
        }

        if (connectivityStatus.backendAvailable !== false) {
          const cachedPartnerSession = getPartnerSessionCache();
          const hasReusableOnlineSession = Boolean(
            this.$store.state.partner_token
            && this.$store.state.partner_token !== 'null'
            && cachedPartnerSession
            && cachedPartnerSession.id
            && cachedPartnerSession.id === verifiedUserRecord.user_id
            && cachedPartnerSession.restaurant_id === verifiedUserRecord.restaurant_id
          );

          if (hasReusableOnlineSession) {
            await clearOfflineSession();
            this.$store.commit('setLoggedIn', true);
            this.$store.dispatch('setUserAllowedToCancel', Boolean(verifiedUserRecord.user_allowed_to_cancel));
            router.push(this.pendingLoginRoute);
            return;
          }

          this.offlineError = 'El acceso rápido solo funciona offline o con una sesión online vigente. Entra con usuario y contraseña para revalidar este usuario.';
          return;
        }

        await createOfflineSessionFromUser(verifiedUserRecord);

        this.$store.commit('setLoggedIn', true);
        this.$store.dispatch('setUserAllowedToCancel', Boolean(verifiedUserRecord.user_allowed_to_cancel));
        router.push({ name: 'TPVHub' });
      } catch (error) {
        console.error(error);
        if ((isBackendUnavailableError(error) || connectivityStatus.backendAvailable === false) && verifiedUserRecord) {
          try {
            await createOfflineSessionFromUser(verifiedUserRecord);
            this.$store.commit('setLoggedIn', true);
            this.$store.dispatch('setUserAllowedToCancel', Boolean(verifiedUserRecord.user_allowed_to_cancel));
            router.push({ name: 'TPVHub' });
            return;
          } catch (fallbackError) {
            console.warn('No se pudo abrir sesión offline tras fallo del backend.', fallbackError);
          }
        }

        if (error?.code === 'offline_login_expired' || error?.code === 'offline_login_locked' || error?.code === 'offline_login_invalid_pin') {
          this.offlineError = error.message;
          return;
        }

        this.offlineError = error?.response?.status === 401 || error?.response?.status === 403
          ? 'Este acceso rápido necesita volver a configurarse en un inicio normal.'
          : (error?.message || 'No se pudo iniciar la sesión.');
      } finally {
        this.hideSpinner();
      }
    },
    async removeOfflineUser(user) {
      if (!user?.user_id || !user?.restaurant_id) return;
      if (!confirm(`¿Eliminar el usuario "${user.display_name}" del acceso rápido de este dispositivo?`)) return;

      try {
        const deleted = await deleteOfflineUserProfile({
          restaurantId: user.restaurant_id,
          userId: user.user_id,
          deviceId: getOrCreateDeviceId(),
        });

        if (!deleted) {
          this.offlineError = 'No se pudo eliminar el usuario guardado.';
          return;
        }

        const wasSelected = this.selectedOfflineUserId === user.user_id;
        this.offlineUsers = this.offlineUsers.filter((offlineUser) => offlineUser.user_id !== user.user_id);

        if (wasSelected) {
          this.selectedOfflineUserId = this.offlineUsers[0]?.user_id || '';
          this.offlinePin = '';
          this.closePinKeypad();
        }

        if (!this.offlineUsers.length) {
          this.loginMode = 'normal';
          this.loginModeManuallySelected = false;
        }
      } catch (error) {
        console.error(error);
        this.offlineError = error?.message || 'No se pudo eliminar el usuario guardado.';
      }
    },
    skipOfflinePinSetup() {
      this.showOfflinePinSetup = false;
      this.offlineSetupPin = '';
      this.offlineSetupPinConfirm = '';
      this.offlineSetupError = '';
      this.pendingOfflineUser = null;
      this.closePinKeypad();
      router.push(this.pendingLoginRoute);
    },
    async confirmOfflinePinSetup() {
      this.offlineSetupError = '';
      if (this.offlineSetupPin.length < 4) {
        this.offlineSetupError = 'El PIN debe tener al menos 4 dígitos.';
        return;
      }
      if (this.offlineSetupPin !== this.offlineSetupPinConfirm) {
        this.offlineSetupError = 'Los PIN no coinciden.';
        return;
      }
      try {
        await enableOfflineLoginForUser({
          user: this.pendingOfflineUser,
          pin: this.offlineSetupPin,
          deviceId: getOrCreateDeviceId(),
          loginIdentifier: this.userLogin.email,
        });
        this.skipOfflinePinSetup();
      } catch (error) {
        this.offlineSetupError = error?.message || 'No se pudo activar el acceso offline.';
      }
    },
    async ChangePass() {
      if (!this.email_change) {
        this.response = 'Introduce un correo correcto';
        return;
      }

      this.showSpinner();

      const apiUrl = `${process.env.VUE_APP_API_URL}/v1/api/partner/ChangePassword?email=${this.email_change}`;
      axios.post(apiUrl)
        .then(() => {
          this.sended = true;
          this.hideSpinner();
          setTimeout(() => {
            this.sended = false;
            this.changing_pass = false;
            this.email_change = '';
            this.response = '';
          }, 5000);
        })
        .catch(() => {
          this.response = 'El email no corresponde a ningún cliente';
          this.hideSpinner();
        });
    },
    setSaludo() {
      const hora = new Date().getHours();
      if (hora >= 6 && hora < 13) {
        this.saludo = '¡Buenos días!';
      } else if (hora >= 13 && hora < 20) {
        this.saludo = '¡Buenas tardes!';
      } else {
        this.saludo = '¡Buenas noches!';
      }
    },
  }
};
</script>

<style scoped>
.login-input{
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr) 36px;
  height: 60px;
  padding: 0 14px 0 18px;
  font-size: 1.1rem;
  font-weight: bold;
  align-items: center;
  box-shadow: 0 0 5px rgba(0,0,0,0.1);
  border: 2px solid #dbdbdb;
  border-radius: 6px;
  color: #0A2533;
  background: #fff;
  gap: 14px;
}

.login-input i {
  width: 24px;
  text-align: center;
  font-size: 1.05rem;
}

.login-input input{
  min-width: 0;
  width: 100%;
  height: 100%;
  box-shadow: none;
  border: 0px;
  background: transparent;
  color: #0A2533;
  font-size: 1.1rem;
  font-weight: 800;
  padding: 0;
}

.login-input input:focus{
  border: 0px !important;
  outline: none;
}

.login-input input:-webkit-autofill,
.login-input input:-webkit-autofill:hover,
.login-input input:-webkit-autofill:focus,
.login-input input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #fff inset !important;
  box-shadow: 0 0 0 1000px #fff inset !important;
  -webkit-text-fill-color: #0A2533 !important;
  caret-color: #0A2533;
  transition: background-color 9999s ease-in-out 0s;
}

.login-password-toggle {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #555555;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.login-password-toggle i {
  width: auto;
  font-size: 1rem;
}

.login-password-toggle:hover {
  background: #F1F1F1;
  color: var(--primary-color);
}

.decorative-text {
  display: flex;
  align-items: center;
  text-align: center;
}

.decorative-text::before,
.decorative-text::after {
  content: '';
  flex: 1;
  border-bottom: 2px solid orange;
  margin: 0 10px;
  height: 0;
}

.decorative-text::before {
  border-left: 50px solid transparent;
}

.decorative-text::after {
  border-right: 50px solid transparent;
}

.decorative-text a {
  font-size: 18px;
  font-weight: bold;
  color: #555555;
}

.login-image{
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.login-image-column {
  position: relative;
  min-height: 280px;
  overflow: hidden;
}

.login-image-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  pointer-events: none;
  padding-top: 18%;
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.26) 100%);
}

.login-image-overlay__logo {
  width: min(62%, 340px);
  max-width: 340px;
  height: auto;
  padding: 14px 18px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.16);
  backdrop-filter: blur(6px);
  box-shadow:
    0 14px 28px rgba(0, 0, 0, 0.28),
    0 0 0 1px rgba(255, 255, 255, 0.18) inset,
    0 0 30px rgba(244, 119, 0, 0.14);
  filter: drop-shadow(0 12px 26px rgba(0, 0, 0, 0.46)) brightness(1.08) saturate(1.05);
  opacity: 1;
}

.login-div{
  padding: 0px 250px 0px 250px;
  height: 100vh;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(244, 119, 0, 0.10), transparent 34%),
    radial-gradient(circle at bottom right, rgba(15, 23, 42, 0.05), transparent 30%),
    #F9F9F9;
}

.login-div::before,
.login-div::after {
  content: '';
  position: absolute;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(18px);
  opacity: 0.42;
}

.login-div::before {
  top: -80px;
  right: -90px;
  background: radial-gradient(circle, rgba(244, 119, 0, 0.22) 0%, rgba(244, 119, 0, 0.02) 72%, transparent 100%);
}

.login-div::after {
  bottom: -120px;
  left: -110px;
  background: radial-gradient(circle, rgba(15, 23, 42, 0.09) 0%, rgba(15, 23, 42, 0.02) 72%, transparent 100%);
}

.login-div > * {
  position: relative;
  z-index: 1;
}

.login-mobile-hero {
  display: none;
  width: 100%;
  margin: 0 0 18px;
}

.login-mobile-hero__media {
  position: relative;
  min-height: 170px;
  border-radius: 28px;
  overflow: hidden;
  background-size: cover;
  background-position: center center;
  box-shadow: 0 22px 50px rgba(15, 23, 42, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.24);
}

.login-mobile-hero__overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.86) 0%, rgba(15, 23, 42, 0.40) 52%, rgba(244, 119, 0, 0.18) 100%),
    radial-gradient(circle at 75% 25%, rgba(255, 255, 255, 0.18), transparent 28%);
}

.login-mobile-hero__content {
  position: relative;
  z-index: 1;
  min-height: 170px;
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 12px;
}

.login-mobile-hero__logo {
  width: min(72%, 220px);
  height: auto;
  padding: 10px 14px;
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.28);
  backdrop-filter: blur(8px);
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(255,255,255,0.12) inset;
}

.login-mobile-hero__copy {
  color: #ffffff;
}

.login-mobile-hero__eyebrow {
  margin: 0 0 4px;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.78);
}

.login-mobile-hero__title {
  margin: 0;
  font-size: 1.2rem;
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.03em;
}

.login-mobile-hero__subtitle {
  margin: 6px 0 0;
  color: rgba(255, 255, 255, 0.86);
  font-size: 0.92rem;
  line-height: 1.35;
}

.login-mode-card {
  width: min(520px, 100%);
  margin: 0 0 22px;
  padding: 18px 18px 16px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 250, 243, 0.98) 100%);
  border: 1px solid rgba(244, 119, 0, 0.14);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
  backdrop-filter: blur(6px);
}

.login-mode-card__copy {
  margin-bottom: 14px;
}

.login-mode-card__eyebrow {
  margin: 0 0 4px;
  color: #f47700;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.login-mode-card__title {
  margin: 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 900;
}

.login-mode-card__subtitle {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.45;
}

.offline-login {
  width: min(520px, 100%);
  margin-top: 0;
  padding-top: 8px;
  z-index: 1;
}

.login-div > form {
  width: min(520px, 100%);
  z-index: 1;
}

.login-mode-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
}

.login-mode-switch__button,
.login-mode-switch__button--active {
  background: linear-gradient(135deg, #F47700 0%, #f59e0b 100%);
  color: #ffffff;
  border-color: transparent;
  box-shadow: 0 10px 22px rgba(244, 119, 0, 0.18);
}

.login-mode-switch__button:hover {
  border-color: #F47700;
  color: #F47700;
}

.offline-login__divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.offline-login__divider::before,
.offline-login__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, rgba(244,119,0,0), rgba(244,119,0,0.55), rgba(244,119,0,0));
}

.offline-login__divider span {
  color: #f47700;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.offline-users {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.offline-user {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 42px;
  gap: 8px;
  align-items: stretch;
}

.offline-user__main {
  width: 100%;
  text-align: left;
  border: 1px solid #dbe2ea;
  border-radius: 10px;
  background: #fff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.offline-user__delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f1b4b4;
  border-radius: 10px;
  background: #fff5f5;
  color: #c53030;
  cursor: pointer;
}

.offline-user__delete:hover {
  background: #ffe7e7;
  border-color: #e58b8b;
}

.offline-user__main strong {
  color: #0f172a;
}

.offline-user__main span {
  color: #64748b;
  font-size: 0.9rem;
}

.offline-user--active {
  box-shadow: 0 0 0 2px rgba(244, 119, 0, 0.12);
}

.offline-user--active .offline-user__main {
  border-color: #F47700;
  box-shadow: 0 0 0 2px rgba(244, 119, 0, 0.12);
  transform: translateY(-1px);
}

.pin-keypad-backdrop {
  position: fixed;
  inset: 0;
  z-index: 12000;
  background: rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 18px;
}

.pin-keypad {
  width: min(420px, 100%);
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 28px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.28);
  padding: 18px;
}

.pin-keypad__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.pin-keypad__eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 800;
}

.pin-keypad__title {
  margin: 2px 0 0;
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 800;
}

.pin-keypad__close {
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 12px;
  background: #eef2f7;
  color: #334155;
}

.pin-keypad__display {
  margin-top: 14px;
  margin-bottom: 14px;
  height: 58px;
  border-radius: 18px;
  background: #0f172a;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  letter-spacing: 0.28em;
  font-weight: 800;
}

.pin-keypad__display span {
  padding-left: 0.28em;
}

.pin-keypad__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.pin-keypad__key {
  height: 64px;
  border: 0;
  border-radius: 18px;
  background: #ffffff;
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 800;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.pin-keypad__key:active {
  transform: translateY(1px);
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.08);
}

.pin-keypad__footer {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

.pin-keypad__action {
  flex: 1;
  height: 48px;
  border: 0;
  border-radius: 14px;
  font-weight: 800;
}

.pin-keypad__action--ghost {
  background: #e2e8f0;
  color: #334155;
}

.pin-keypad__action--primary {
  background: linear-gradient(135deg, #F47700 0%, #f59e0b 100%);
  color: #ffffff;
}

.pin-keypad-fade-enter-active,
.pin-keypad-fade-leave-active {
  transition: opacity 0.18s ease;
}

.pin-keypad-fade-enter-from,
.pin-keypad-fade-leave-to {
  opacity: 0;
}

.page{
  overflow: hidden;
}

.modal {
  background: rgba(139, 139, 139, 0.5);
}
.modal-dialog {
  min-height: 90vh;
}

@media screen and (max-width: 1700px) {
  .login-div{
    padding: 0px 220px 0px 220px;
  }
}

@media screen and (max-width: 1500px) {
  .login-div{
    padding: 0px 180px 0px 180px;
  }
}

@media screen and (max-width: 1350px) {
  .login-div{
    padding: 0px 150px 0px 150px;
  }
}

@media screen and (max-width: 1200px) {
  .login-div{
    padding: 0px 100px 0px 100px;
  }
}

@media screen and (max-width: 991.98px) {
  .login-image-column {
    display: none;
  }

  .login-div {
    height: auto;
    min-height: 100vh;
    padding: 30px 40px 34px 40px;
    justify-content: flex-start;
    gap: 16px;
  }

  .login-mobile-hero {
    display: block;
    margin-bottom: 18px;
  }

  .login-mobile-hero__media {
    min-height: 220px;
    border-radius: 30px;
  }

  .login-mobile-hero__content {
    min-height: 220px;
    padding: 24px 24px 22px;
  }

  .login-mobile-hero__logo {
    width: min(68%, 240px);
  }

  .login-mobile-hero__title {
    font-size: 1.4rem;
  }

  .login-mobile-hero__subtitle {
    max-width: 28rem;
    font-size: 0.98rem;
  }

  .login-mode-card,
  .offline-login,
  .login-div > form {
    width: 100%;
  }

  .login-mode-card {
    margin-bottom: 16px;
    padding: 20px 20px 18px;
  }

  .login-mode-switch {
    gap: 12px;
  }

  .login-mode-switch .btn {
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  .login-input {
    height: 58px;
    border-radius: 14px;
  }

  .offline-user {
    grid-template-columns: minmax(0, 1fr) 44px;
  }

  .offline-user__main {
    padding: 13px 14px;
    border-radius: 12px;
  }

  .offline-user__delete {
    width: 44px;
    border-radius: 12px;
  }

  .pin-keypad {
    width: min(520px, 100%);
  }
}

@media screen and (max-width: 576px) {
  .login-div{
    height: auto;
    min-height: 100vh;
    padding: 18px 14px 22px 14px;
    justify-content: flex-start;
    gap: 14px;
  }

  .login-mobile-hero {
    display: block;
  }

  .login-mode-card {
    width: 100%;
    margin-bottom: 14px;
    padding: 16px 14px 14px;
  }

  .login-mode-card,
  .offline-login,
  .login-div > form {
    width: 100%;
  }

  .login-mode-switch {
    grid-template-columns: 1fr;
  }

  .login-mode-switch .btn {
    width: 100%;
    justify-content: center;
  }

  .login-mode-card__title {
    font-size: 1rem;
  }

  .login-mode-card__subtitle {
    font-size: 0.88rem;
  }

  .offline-login {
    padding-top: 2px;
  }

  .login-div > form {
    margin-top: 0;
  }

  .login-input {
    height: 56px;
    padding: 0 12px 0 14px;
    border-radius: 12px;
  }

  .login-input input {
    font-size: 1rem;
  }

  .offline-user {
    grid-template-columns: minmax(0, 1fr) 40px;
    gap: 6px;
  }

  .offline-user__main {
    padding: 11px 12px;
  }

  .offline-user__delete {
    width: 40px;
  }

  .pin-keypad {
    padding: 16px;
    border-radius: 24px;
  }

  .pin-keypad__key {
    height: 58px;
    border-radius: 16px;
  }
}

@media screen and (max-width: 500px) {
  .login-div{
    padding: 16px 12px 20px 12px;
  }

  .login-mobile-hero__media {
    min-height: 160px;
    border-radius: 24px;
  }

  .login-mobile-hero__content {
    min-height: 160px;
    padding: 16px 14px 14px;
  }

  .login-mobile-hero__logo {
    width: min(76%, 200px);
    padding: 9px 12px;
    border-radius: 16px;
  }

  .login-mobile-hero__title {
    font-size: 1.08rem;
  }

  .login-mobile-hero__subtitle {
    font-size: 0.86rem;
  }
}
</style>
